import React, { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { Map } from 'immutable';

// MUI components
import { Grid, Card, CardContent, Hidden } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ViewList } from '@material-ui/icons';
import Header from 'components/Header';
import Table from 'components/Table';
import ExportButton from 'components/ExportButton';
import ReportRefreshButton from 'components/ReportRefreshButton';

// action creators
import { createLeadsInfoReport } from 'store/actions/apiTaskActions';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getForm from 'store/selectors/getForm';
import getSliceEntityById from 'store/selectors/getSliceEntityById';

const REPORT_FORM = 'leadsInfoReportForm';

export default function LeadsInfoCard() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const initialOrder = 'asc';
  const initialOrderBy = 'leadFullName';
  const {
    currentGroupId,
    report: {
      data: { content = [], meta: { columns = [] } = {} } = {},
      id: reportId,
    },
    form: { isSubmitting },
  } = useSelector(
    state => ({
      currentGroupId: getCurrentGroupId(state),
      form: getForm(REPORT_FORM)(state),
      report: getSliceEntityById(
        'report',
        'ReportTypes.LEADS_INFO',
        Map()
      )(state),
    }),
    isEqual
  );

  const dispatchCreateReport = useCallback(
    ({ forceRefresh }: { forceRefresh: boolean }) => {
      dispatch(
        createLeadsInfoReport({ groupId: currentGroupId, forceRefresh })
      );
    },
    [dispatch, currentGroupId]
  );

  useEffect(() => {
    dispatchCreateReport({ forceRefresh: false });
  }, [dispatchCreateReport]);

  const tableData = useMemo(() => {
    if (!content) return [];
    if (isSubmitting || !content.length) return [];
    return content.map(({ leadId, ...rest }: any) => ({
      key: leadId,
      ...rest,
    }));
  }, [content, isSubmitting]);
  return (
    <Card>
      <CardContent className={classes.cardContent}>
        <Grid container>
          <Grid item xs>
            <Header title='Leads Info' icon={ViewList} compact />
          </Grid>

          <Hidden xsDown>
            <Grid item xs={4} md={2}>
              <Grid container justifyContent='flex-end'>
                <Grid item>
                  <ReportRefreshButton
                    loading={isSubmitting}
                    onRefresh={dispatchCreateReport}
                  />
                </Grid>
                <ExportButton params={{ reportId }} disabled={isSubmitting} />
              </Grid>
            </Grid>
          </Hidden>

          <Grid item xs={12}>
            <Table
              columns={columns}
              data={tableData}
              variant='outlined'
              numberOfSkeletonRows={isSubmitting ? 8 : 0}
              initialOrder={initialOrder}
              initialOrderBy={initialOrderBy}
              TableRowProps={{
                classes: {
                  root: (classes as any).root,
                },
              }}
              TableCellProps={{
                className: (classes as any).tableCell,
              }}
              HeaderTableCellProps={{
                className: (classes as any).headerTableCell,
              }}
              TablePaginationProps={{
                count: content.length,
                rowsPerPage: 10,
                rowsPerPageOptions: [10],
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
const useStyles = makeStyles(theme => ({
  cardContent: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  exportButton: {
    textAlign: 'right',
  },
}));
